import { useAtomValue, useSetAtom } from 'jotai';
import { usePostHog } from 'posthog-js/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { env } from '@/constants/env';
import { uploadImage as uploadImageToEstateMedia } from '@/features/estate-media/api/upload-image';
import { getImage } from '@/features/image-rendering/api/image';
import { useRenderImage } from '@/features/image-rendering/api/render-image';
import { selectedImageAtom, uploadedImagesAtom } from '@/features/image-rendering/atoms/imageFiles';
import {
  additionalInfoAtom,
  desiredColorSettingsAtom,
  desiredStyleSettingsAtom,
  getCheckboxByValueAtom,
  goodWeatherDaylightAtom,
  goodWeatherSeasonAtom,
  materialSettingsAtom,
  roomModeSettingsAtom,
  roomTypeSettingsAtom,
  settingsChangedAtom,
  watermarkAtom,
} from '@/features/image-rendering/atoms/settings';
import { uploadImage as uploadImageToProjectMedia } from '@/features/project-media/api/upload-image';
import type { RenderingImage } from '@/features/renderings/shared/atoms/image-files';
import {
  renderingsSelectedPreviewImageAtom,
  renderingsUploadedImagesAtom,
} from '@/features/renderings/shared/atoms/image-files';
import { createTempId } from '@/utils/tempId';
import { addWatermarkToFile } from '@/utils/watermark';

export const useAiRenderingCreate = ({
  estateId,
  projectId,
}: {
  estateId?: string;
  projectId?: string;
}) => {
  const { t } = useTranslation();
  const posthog = usePostHog();

  const [isLoading, setIsLoading] = useState(false);

  const selectedImage = useAtomValue(selectedImageAtom);
  const setUploadedImages = useSetAtom(uploadedImagesAtom);
  const setRenderingsUploadedImages = useSetAtom(renderingsUploadedImagesAtom);
  const setSelectedPreviewImage = useSetAtom(renderingsSelectedPreviewImageAtom);
  const setSettingsChanged = useSetAtom(settingsChangedAtom);

  const roomtype = useAtomValue(roomTypeSettingsAtom);
  const colors = useAtomValue(desiredColorSettingsAtom);
  const style = useAtomValue(desiredStyleSettingsAtom);
  const mode = useAtomValue(roomModeSettingsAtom);
  const additionalInfo = useAtomValue(additionalInfoAtom);
  const materials = useAtomValue(materialSettingsAtom);
  const goodWeatherDaylight = useAtomValue(goodWeatherDaylightAtom);
  const goodWeatherSeason = useAtomValue(goodWeatherSeasonAtom);
  const getCheckbox = useAtomValue(getCheckboxByValueAtom);
  const watermarkInfo = useAtomValue(watermarkAtom);

  const { mutateAsync: renderImage } = useRenderImage({
    mutationConfig: {
      onSuccess: (data) => {
        const socket = new WebSocket(
          `${env.API_URL.replace('http', 'ws')}/ws?taskId=${data.taskId}`
        );
        toast.success(t('rendering-initiated'));

        const parseJsonError = async (event: MessageEvent) => {
          try {
            if (event.data instanceof Blob) {
              const textData = await event.data.text();
              const parsed: unknown = JSON.parse(textData);
              if (!parsed || typeof parsed !== 'object') return null;
              const error = (parsed as Record<string, unknown>).error;
              return typeof error === 'string' ? error : null;
            }
            return null;
          } catch {
            return null;
          }
        };

        const processImageData = async (event: MessageEvent) => {
          const generatedFile = new File([event.data], 'image', { type: 'image/jpeg' });
          const watermarkedImage = watermarkInfo.applyWatermark
            ? await addWatermarkToFile(generatedFile, watermarkInfo.text, watermarkInfo.position)
            : null;
          const image = watermarkedImage ?? generatedFile;
          const imgUrl = URL.createObjectURL(image);
          let renderedImageId: string | undefined;

          if (estateId) {
            const uploadedImage = await uploadImageToEstateMedia({
              image,
              estateId,
            });
            renderedImageId = uploadedImage[0]?.mediaId ?? undefined;
          } else if (projectId) {
            const uploadedImage = await uploadImageToProjectMedia({
              image,
              projectId,
            });
            renderedImageId = uploadedImage[0]?.mediaId ?? undefined;
          }

          if (!selectedImage) return;

          const generatedImage: RenderingImage = {
            ...selectedImage,
            id: createTempId(),
            file: image,
            renderedImage: imgUrl,
            renderedImageId,
            renderingType: 'ai-rendering',
            watermarkApplied: Boolean(watermarkedImage),
          };

          setUploadedImages((prev) => {
            const curr = prev.find((img) => img.id === selectedImage?.id);
            if (!curr) return prev;

            return [...prev, generatedImage];
          });

          setRenderingsUploadedImages((prev) => [...prev, generatedImage]);
          setSelectedPreviewImage(generatedImage);
          setSettingsChanged(false);
          toast.success(t('rendering-complete'));
          posthog.capture('rendered_image', { data_id: estateId });
          posthog.capture('image-rendering-created', { estateId, projectId });
        };

        socket.onmessage = async (event) => {
          try {
            const errorMessage = await parseJsonError(event);
            if (errorMessage) {
              toast.error(errorMessage);
              setIsLoading(false);
              socket.close();
              return;
            }

            await processImageData(event);
            setIsLoading(false);
            socket.close();
          } catch (error) {
            const message =
              error instanceof Error ? error.message : 'Error processing rendering result';
            toast.error(message);
            setIsLoading(false);
            socket.close();
          }
        };

        socket.onerror = (error) => {
          toast.error(`WebSocket error: ${error}`);
          setIsLoading(false);
          socket.close();
        };
      },
      onError: (error) => {
        toast.error(error.message);
        setIsLoading(false);
      },
    },
  });

  const start = async () => {
    if (!selectedImage) return;

    try {
      setIsLoading(true);

      // If uploaded locally and now associated with an estate/project, upload source first.
      if (selectedImage.file && !selectedImage.originalImageId) {
        if (estateId) {
          await uploadImageToEstateMedia({ image: selectedImage.file, estateId });
        } else if (projectId) {
          await uploadImageToProjectMedia({ image: selectedImage.file, projectId });
        }
      }

      const getImageFile = async (imgId: string | undefined) => {
        if (!imgId) return null;
        const response = await getImage(imgId);
        const blob = await response.blob();
        return new File([blob], 'image', { type: blob.type || 'image/jpeg' });
      };

      const image = selectedImage.file
        ? selectedImage.file
        : await getImageFile(selectedImage.originalImageId);
      if (!image) {
        toast.error(t('rendering-failed-to-get-image'));
        setIsLoading(false);
        return;
      }

      await renderImage({
        image,
        roomtype: roomtype || undefined,
        style,
        colors,
        materials,
        mode,
        keepWall: !!getCheckbox('keepWall') && getCheckbox('keepWall').checked,
        keepFloor: !!getCheckbox('keepFloor') && getCheckbox('keepFloor').checked,
        goodWeatherDaylight,
        goodWeatherSeason,
        additionalInfo,
      });
    } catch {
      toast.error(t('rendering-failed-to-start'));
      setIsLoading(false);
    }
  };

  return { start, isLoading };
};
